// Time display in the user's time zone. Data keeps absolute moments as ISO
// UTC strings; everything shown on screen goes through these helpers with the
// zone from the account settings (IANA id: Europe/Moscow, America/New_York…).
// Date-only values (a purchase date) are NOT moments: see dateOnly* helpers.
// Words and formats follow the UI language (i18n); the zone is the web display zone.
import i18n, { locale } from '@/i18n';
import { MOCK_DATA } from '@/lib/api/mode';

/**
 * «Now». With the ORCA API it is the real clock. The DEV mock (?dev=) keeps the
 * prototype's fixed moment so its scenarios are reproducible; the demo content
 * still shown on pages outside Phase 6A is authored relative to this moment, so
 * with the API it is anchored to the real today instead.
 */
export const MOCK_NOW_ISO = MOCK_DATA ? '2026-10-06T18:40:00Z' : new Date().toISOString();
const started = Date.now();
export const nowMs = () => (MOCK_DATA ? Date.parse(MOCK_NOW_ISO) + (Date.now() - started) : Date.now());
export const nowIso = () => new Date(nowMs()).toISOString();

const fmts = new Map<string, Intl.DateTimeFormat>();
/** Intl formatter in the zone; `loc` — the UI language (calendar maths uses a fixed locale) */
function fmt(tz: string, opts: Intl.DateTimeFormatOptions, loc = 'en-US') { // i18n-allow: parts for date maths
  const key = loc + tz + JSON.stringify(opts);
  let f = fmts.get(key);
  if (!f) { f = new Intl.DateTimeFormat(loc, { timeZone: tz, ...opts }); fmts.set(key, f); }
  return f;
}
const ms = (v: string | number) => (typeof v === 'number' ? v : Date.parse(v));
const cap = (s: string) => s.charAt(0).toLocaleUpperCase(locale()) + s.slice(1);
/** a date-only value as a UTC noon instant (no zone shift) */
const dayMs = (k: string) => { const [y, m, d] = k.split('-').map(Number); return Date.UTC(y, m - 1, d || 1, 12); };
/** formatted in the UI language; strips the Russian « г.» year suffix */
const fmtL = (v: number, tz: string, opts: Intl.DateTimeFormatOptions) => fmt(tz, opts, locale()).format(new Date(v)).replace(/\s?г\.$/, '');

/** Local calendar parts of a moment in a zone. */
export function parts(v: string | number, tz: string) {
  const p = Object.fromEntries(fmt(tz, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(ms(v))).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute };
}
/** 'YYYY-MM-DD' of the local date. */
export const dateKey = (v: string | number, tz: string) => { const p = parts(v, tz); return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`; };
const keyDays = (k: string) => { const [y, m, d] = k.split('-').map(Number); return Date.UTC(y, m - 1, d) / 86400000; };
/** Whole local days between the moment's date and today (0 today, -1 yesterday, 1 tomorrow). */
export const dayDiff = (v: string | number, tz: string) => keyDays(dateKey(v, tz)) - keyDays(dateKey(nowMs(), tz));
export const isToday = (v: string | number, tz: string) => dayDiff(v, tz) === 0;

/** 'HH:MM' (24 h in every language — the product's convention) */
export const time = (v: string | number, tz: string) => { const p = parts(v, tz); return `${String(p.h).padStart(2, '0')}:${String(p.min).padStart(2, '0')}`; };
/** Today / Yesterday / Tomorrow / Monday… */
export function dayLabel(v: string | number, tz: string) {
  const diff = dayDiff(v, tz);
  if (diff === 0) return i18n.t('common:time.today');
  if (diff === -1) return i18n.t('common:time.yesterday');
  if (diff === 1) return i18n.t('common:time.tomorrow');
  return cap(fmtL(dayMs(dateKey(v, tz)), 'UTC', { weekday: 'long' }));
}
/** Mon / Пн / Mo */
export const dayShort = (v: string | number, tz: string) => cap(fmtL(dayMs(dateKey(v, tz)), 'UTC', { weekday: 'short' }).replace(/\.$/, ''));
/** '6 октября' · 'October 6' · '6. Oktober' */
export const dateLong = (v: string | number, tz: string) => fmtL(dayMs(dateKey(v, tz)), 'UTC', { day: 'numeric', month: 'long' });
/** '6 окт.' · 'Oct 6' · '6. Okt.' */
export const dateShort = (v: string | number, tz: string) => fmtL(dayMs(dateKey(v, tz)), 'UTC', { day: 'numeric', month: 'short' });
/** 'сентябрь' · 'September' */
export const monthName = (v: string | number, tz: string) => fmtL(dayMs(dateKey(v, tz)), 'UTC', { month: 'long' });
/** 'вторник, 6 октября' · 'Tuesday, October 6' */
export const dateFull = (v: string | number, tz: string) => fmtL(dayMs(dateKey(v, tz)), 'UTC', { weekday: 'long', day: 'numeric', month: 'long' });
/** '6 октября 2026, 18:40' */
export const dateTime = (v: string | number, tz: string) => `${fmtL(dayMs(dateKey(v, tz)), 'UTC', { day: 'numeric', month: 'long', year: 'numeric' })}, ${time(v, tz)}`;
/** today 18:00 · tomorrow 18:00 · Wed 18:00 · Oct 4 18:00 */
export function when(v: string | number, tz: string) {
  const diff = dayDiff(v, tz);
  const t = time(v, tz);
  if (diff === 0) return i18n.t('common:time.todayAt', { time: t });
  if (diff === -1) return i18n.t('common:time.yesterdayAt', { time: t });
  if (diff === 1) return i18n.t('common:time.tomorrowAt', { time: t });
  if (Math.abs(diff) < 7) return `${dayShort(v, tz)} ${t}`;
  return `${dateShort(v, tz)} ${t}`;
}
const rtf = new Map<string, Intl.RelativeTimeFormat>();
const rel = (n: number, unit: Intl.RelativeTimeFormatUnit) => {
  const l = locale();
  let f = rtf.get(l);
  if (!f) { f = new Intl.RelativeTimeFormat(l, { numeric: 'always', style: 'short' }); rtf.set(l, f); }
  return f.format(n, unit);
};
/** Relative past: now · 12 min ago · 3 hr ago · 2 days ago (Intl, plural-correct) */
export function ago(v: string | number) {
  const m = Math.max(0, Math.round((nowMs() - ms(v)) / 60000));
  if (m < 2) return i18n.t('common:time.now');
  if (m < 60) return rel(-m, 'minute');
  if (m < 60 * 24) return rel(-Math.round(m / 60), 'hour');
  return rel(-Math.round(m / 1440), 'day');
}
/** Relative future: in 2 h 10 min */
export function until(v: string | number) {
  const m = Math.max(0, Math.round((ms(v) - nowMs()) / 60000));
  const h = Math.floor(m / 60);
  return h ? (m % 60 ? i18n.t('common:time.inHM', { h, m: m % 60 }) : i18n.t('common:time.inH', { h })) : i18n.t('common:time.inM', { m });
}
/** Offset label for a zone right now: МСК, UTC, UTC+2, UTC−4, UTC+5:30 */
export function tzShort(tz: string) {
  if (tz === 'Europe/Moscow') return i18n.t('common:time.msk');
  const p = parts(nowMs(), tz);
  const off = Math.round((Date.UTC(p.y, p.m - 1, p.d, p.h, p.min) - Math.floor(nowMs() / 60000) * 60000) / 60000);
  if (!off) return 'UTC';
  const a = Math.abs(off);
  return `UTC${off > 0 ? '+' : '−'}${Math.floor(a / 60)}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : ''}`;
}
/** A wall-clock time in a market zone on a given local date → ISO instant (e.g. 09:00 New York). */
export function zonedToIso(date: string, hhmm: string, tz: string) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  let guess = Date.UTC(y, mo - 1, d, h, mi);
  for (let i = 0; i < 2; i++) {
    const p = parts(guess, tz);
    guess -= Date.UTC(p.y, p.m - 1, p.d, p.h, p.min) - Date.UTC(y, mo - 1, d, h, mi);
  }
  return new Date(guess).toISOString();
}
export const addDays = (key: string, n: number) => new Date((keyDays(key) + n) * 86400000).toISOString().slice(0, 10);

/* ---------- date-only values (no time zone): purchase date ---------- */
/** '2026-09-14' → '14 сентября 2026' · 'September 14, 2026'; '2026-09' → 'сентябрь 2026' */
export function dateOnly(v: string) {
  const [, , d] = v.split('-').map(Number);
  return d ? fmtL(dayMs(v), 'UTC', { day: 'numeric', month: 'long', year: 'numeric' }) : monthLabel(v);
}
/** when a position started: «с 14 сентября 2026» · «≈ с 14 мая 2025» (found by price history) · «примерно с мая 2025» */
export function sinceLabel(v: string, source?: 'user' | 'history' | 'period') {
  const [, , d] = v.split('-').map(Number);
  if (!d || source === 'period') return i18n.t('common:date.sinceApprox', { month: monthLabel(v.slice(0, 7)) });
  return `${source === 'history' ? '≈ ' : ''}${i18n.t('common:date.since', { date: dateOnly(v) })}`;
}
/** a purchase in a compact list: '14 сен 2026' · '≈ 14 мая 2025' · 'май 2025, примерно' */
export function lotDateLabel(l: { date?: string; dateSource?: 'user' | 'history'; period?: string }) {
  if (l.date) return `${l.dateSource === 'history' ? '≈ ' : ''}${fmtL(dayMs(l.date), 'UTC', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  if (l.period) return i18n.t('common:date.periodApprox', { month: monthLabel(l.period) });
  return i18n.t('common:date.noDate');
}
/** '2025-05' → 'май 2025' · 'May 2025' · 'Mai 2025' (standalone month name) */
export const monthLabel = (period: string) => cap(fmtL(dayMs(period), 'UTC', { month: 'long', year: 'numeric' }));
/** comparable key of a date-only value ('2026-09' → '2026-09-01') */
export const dateOnlyKey = (v: string) => (v.length === 7 ? `${v}-01` : v);
/** whole days between two date keys */
export const daysBetween = (a: string, b: string) => keyDays(b) - keyDays(a);
/** today's date in the zone as 'YYYY-MM-DD' (max for date pickers) */
export const todayKey = (tz: string) => dateKey(nowMs(), tz);
/** month names of the UI language: 'long' standalone (Январь · January · Januar) or 'short' (янв · Jan · Jan) */
export const monthNames = (style: 'long' | 'short') => Array.from({ length: 12 }, (_, i) => cap(fmtL(Date.UTC(2026, i, 15, 12), 'UTC', { month: style }).replace(/\.$/, '')));
/** short weekday names, Monday first (Пн · Mon · Mo) */
export const weekdayNames = () => Array.from({ length: 7 }, (_, i) => cap(fmtL(Date.UTC(2026, 9, 5 + i, 12), 'UTC', { weekday: 'short' }).replace(/\.$/, '')));
/** a date-only value in compact numeric form: 14.09.26 · 09/14/26 (a month: 09.26 · 09/26) */
export const dateOnlyShort = (v: string) => fmtL(dayMs(v), 'UTC', v.length === 7 ? { month: '2-digit', year: '2-digit' } : { day: '2-digit', month: '2-digit', year: '2-digit' });
